import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Briefcase,
  Users,
  Package,
  DollarSign,
  TrendingUp,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  ArrowRight,
  Plus
} from 'lucide-react';

export const Dashboard = () => {
  const { user, api, isCOO } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, jobsRes, progRes] = await Promise.all([
          api.get('/portal/dashboard'),
          api.get('/jobs?limit=5'),
          api.get('/project-management/progress-summary')
        ]);
        setDashboard(dashRes.data);
        setRecentJobs(jobsRes.data.slice(0, 5));
        setProgress(progRes.data || []);
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-500';
      case 'active': return 'bg-blue-500/10 text-blue-500';
      case 'pursuing': return 'bg-yellow-500/10 text-yellow-500';
      case 'bid_submitted': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'green': return 'text-green-500';
      case 'yellow': return 'text-yellow-500';
      case 'red': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl text-gray-900">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-gray-500 mt-1">{user?.title}</p>
        </div>
        <Link
          to="/portal/jobs/new"
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Job
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-500" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Active Jobs</span>
          </div>
          <p className="font-heading text-3xl">{dashboard?.jobs?.active || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-yellow-500/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-yellow-500" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Pursuing</span>
          </div>
          <p className="font-heading text-3xl">{dashboard?.jobs?.pursuing || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Completed</span>
          </div>
          <p className="font-heading text-3xl">{dashboard?.jobs?.completed || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#C6A87C]/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#C6A87C]" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Projected</span>
          </div>
          <p className="font-heading text-3xl">${(dashboard?.revenue?.projected || 0).toLocaleString()}</p>
        </motion.div>
      </div>

      {/* Active Projects Progress */}
      {progress.length > 0 && (
        <div className="bg-white border border-gray-100 p-6" data-testid="dashboard-progress-section">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading text-xl">Active Projects Progress</h2>
            <Link
              to="/portal/progress"
              className="text-xs font-semibold uppercase tracking-widest text-[#C6A87C] hover:underline flex items-center gap-1"
            >
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-5">
            {progress.slice(0, 5).map((proj) => (
              <Link
                to="/portal/progress"
                key={proj.job_id}
                className="block group"
                data-testid={`dashboard-progress-${proj.job_id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800 group-hover:text-[#C6A87C] transition-colors truncate pr-3">
                    {proj.client_name} <span className="text-gray-400 font-normal">• {proj.project_type}</span>
                  </span>
                  <span className="text-sm font-semibold text-gray-900 flex-shrink-0">{proj.progress_percent}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${proj.progress_percent >= 100 ? 'bg-green-500' : proj.progress_percent >= 50 ? 'bg-[#C6A87C]' : 'bg-blue-500'}`}
                    style={{ width: `${proj.progress_percent}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {proj.completed_tasks} / {proj.total_tasks} tasks completed
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Jobs */}
        <div className="lg:col-span-2 bg-white border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading text-xl">Recent Jobs</h2>
            <Link
              to="/portal/jobs"
              className="text-xs font-semibold uppercase tracking-widest text-[#C6A87C] hover:underline flex items-center gap-1"
            >
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          
          {recentJobs.length > 0 ? (
            <div className="space-y-4">
              {recentJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/portal/jobs/${job.id}`}
                  className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-medium">{job.client_name}</h3>
                    <p className="text-gray-500 text-sm">{job.project_type} • {job.city}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-3 py-1 text-xs font-semibold uppercase ${getStatusColor(job.status)}`}>
                      {job.status.replace('_', ' ')}
                    </span>
                    <p className="text-gray-500 text-sm mt-1">${job.estimated_value?.toLocaleString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No jobs yet</p>
              <Link to="/portal/jobs/new" className="text-[#C6A87C] text-sm mt-2 inline-block hover:underline">
                Create your first job
              </Link>
            </div>
          )}
        </div>

        {/* Financial Summary */}
        <div className="bg-white border border-gray-100 p-6">
          <h2 className="font-heading text-xl mb-6">Financial Summary</h2>
          
          {isCOO ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-100">
                <div className="flex items-center gap-2 text-green-700 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase">Net Profit</span>
                </div>
                <p className="font-heading text-2xl text-green-700">
                  ${(dashboard?.financials?.net_profit || 0).toLocaleString()}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">Income</p>
                  <p className="font-semibold">${(dashboard?.financials?.total_income || 0).toLocaleString()}</p>
                </div>
                <div className="p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">Expenses</p>
                  <p className="font-semibold">${(dashboard?.financials?.total_expenses || 0).toLocaleString()}</p>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-100">
                <div className="flex items-center gap-2 text-yellow-700 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase">Pending AR</span>
                </div>
                <p className="font-heading text-xl text-yellow-700">
                  ${(dashboard?.financials?.pending_invoices || 0).toLocaleString()}
                </p>
              </div>

              <div className="p-4 bg-red-50 border border-red-100">
                <div className="flex items-center gap-2 text-red-700 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase">Taxes Due</span>
                </div>
                <p className="font-heading text-xl text-red-700">
                  ${(dashboard?.financials?.taxes_due || 0).toLocaleString()}
                </p>
              </div>

              <Link
                to="/portal/accounting"
                className="block text-center py-3 border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                View Full Accounting
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50">
                <p className="text-xs text-gray-500 mb-1">Projected Revenue</p>
                <p className="font-heading text-2xl">${(dashboard?.financials?.projected_revenue || 0).toLocaleString()}</p>
              </div>

              <div className="p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold uppercase text-gray-500">Health Status</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full ${dashboard?.financials?.health_status === 'green' ? 'bg-green-500' : dashboard?.financials?.health_status === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                  <span className={`font-semibold capitalize ${getHealthColor(dashboard?.financials?.health_status)}`}>
                    {dashboard?.financials?.health_status || 'Unknown'}
                  </span>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Contact RJ for detailed financial reports
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link
          to="/portal/jobs"
          className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors"
        >
          <Briefcase className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Jobs Pipeline</span>
        </Link>
        <Link
          to="/portal/team"
          className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors"
        >
          <Users className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Team Builder</span>
        </Link>
        <Link
          to="/portal/inventory"
          className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors"
        >
          <Package className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Inventory</span>
        </Link>
        <Link
          to="/portal/documents"
          className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors"
        >
          <FileText className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Documents</span>
        </Link>
      </div>
    </div>
  );
};
